import { useState, useMemo } from 'react';
import { Dashboard } from './components/Dashboard';
import { EntryForm } from './components/EntryForm';
import { Settings } from './components/Settings';
import { Auth } from './components/Auth';
import { EffortTracker } from './components/EffortTracker';
import { VacationTracker } from './components/VacationTracker';
import { Supervisor } from './components/Supervisor';
import { NotificationCenter } from './components/NotificationCenter';
import { useAuth } from './contexts/AuthContext';
import { Clock, LayoutDashboard, Settings as SettingsIcon, LogOut, Target, Palmtree, ShieldCheck, User } from 'lucide-react';
import packageJson from '../package.json';

type View = 'dashboard' | 'efforts' | 'vacations' | 'entry' | 'settings' | 'supervisor';

function App() {
  const { session, loading, signOut, isSupervisor, isSupervisorMode, toggleViewMode } = useAuth();
  
  const [activeView, setActiveView] = useState<View>('dashboard');
  const [editEntryId, setEditEntryId] = useState<string | null>(null);

  // Derive current view cleanly without setState in effects
  const currentView = useMemo<View>(() => {
    if (!session) return 'dashboard';
    if (isSupervisorMode) {
      if (activeView === 'dashboard' || activeView === 'vacations') {
        return 'supervisor';
      }
    } else if (activeView === 'supervisor') {
      return 'dashboard';
    }
    return activeView;
  }, [session, isSupervisorMode, activeView]);

  const setCurrentView = (view: View) => setActiveView(view);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <Clock className="w-12 h-12 text-indigo-300 mb-4" />
          <p className="text-slate-400 font-medium">Loading JaTT...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  const handleEdit = (id: string) => {
    setEditEntryId(id);
    setCurrentView('entry');
  };

  const handleNew = () => {
    setEditEntryId(null);
    setCurrentView('entry');
  };

  const handleToggleViewMode = () => {
    if (isSupervisorMode) {
      toggleViewMode();
      setCurrentView('dashboard');
    } else {
      toggleViewMode();
      setCurrentView('supervisor');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="w-[90%] mx-auto">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="bg-indigo-600 p-2 rounded-lg text-white" title={`Version ${packageJson.version}`}>
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-bold text-xl tracking-tight text-slate-800">JaTT</span>
                <span className="text-xs font-medium text-slate-400 select-none">v{packageJson.version}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {isSupervisorMode ? (
                <button
                  onClick={() => setCurrentView('supervisor')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'supervisor' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span className="hidden sm:inline">Supervisor</span>
                </button>
              ) : (
                <button
                  onClick={() => setCurrentView('dashboard')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'dashboard' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </button>
              )}
              <button
                onClick={() => setCurrentView('efforts')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'efforts' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                <Target className="w-4 h-4" />
                <span className="hidden sm:inline">Efforts</span>
              </button>
              {!isSupervisorMode && (
                <button
                  onClick={() => setCurrentView('vacations')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'vacations' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                >
                  <Palmtree className="w-4 h-4" />
                  <span className="hidden sm:inline">Vacation Days</span>
                </button>
              )}
              <button
                onClick={() => setCurrentView('settings')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'settings' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                <SettingsIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Settings</span>
              </button>

              {/* View Switcher Button: visible only to supervisors */}
              {isSupervisor && (
                <button
                  onClick={handleToggleViewMode}
                  title={isSupervisorMode ? 'Switch to User View' : 'Switch to Supervisor View'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border shadow-xs ${
                    isSupervisorMode
                      ? 'bg-slate-100 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border-slate-200 hover:border-indigo-200'
                      : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200 shadow-sm'
                  }`}
                >
                  {isSupervisorMode ? (
                    <>
                      <User className="w-3.5 h-3.5 text-slate-500" />
                      <span className="hidden sm:inline">Switch to User View</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
                      <span className="hidden sm:inline">Switch to Supervisor View</span>
                    </>
                  )}
                </button>
              )}

              {/* Notification Center */}
              <NotificationCenter
                onNavigate={(view) => {
                  if (view === 'supervisor') {
                    if (!isSupervisorMode && isSupervisor) {
                      toggleViewMode();
                    }
                    setCurrentView('supervisor');
                  } else if (view === 'dashboard') {
                    if (isSupervisorMode) {
                      toggleViewMode();
                    }
                    setCurrentView('dashboard');
                  }
                }}
              />

              <div className="h-6 w-px bg-slate-200 mx-1"></div>
              <button
                onClick={signOut}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="w-[90%] mx-auto py-8">
        {currentView === 'dashboard' && <Dashboard onNew={handleNew} onEdit={handleEdit} />}
        {currentView === 'efforts' && <EffortTracker />}
        {currentView === 'vacations' && <VacationTracker />}
        {currentView === 'entry' && (
          <EntryForm
            entryId={editEntryId}
            onSave={() => setCurrentView(isSupervisorMode ? 'supervisor' : 'dashboard')}
            onCancel={() => setCurrentView(isSupervisorMode ? 'supervisor' : 'dashboard')}
          />
        )}
        {currentView === 'settings' && <Settings />}
        {currentView === 'supervisor' && <Supervisor onEdit={handleEdit} />}
      </main>
    </div>
  );
}

export default App;
